// MAIN ROOM WRAPPER. IT CONTAINS ALL THE LOGIC NEEDED TO CONNECT OUTGOING OR INCOMING CALLS.
//
// Ported from qr-phone-frontend src/app/modules/calling/VideoState.js (v3.2.0.0,
// merge ae40b42: real ICE restart #8, resilient reconnection #9, call queue v3.1.0.0).
// The signaling protocol (event names, payloads, timings, RTDB writes) is UNCHANGED.
// Adaptations for this app, all glue-level:
//   - Redux reads replaced: turnDetails -> core/iceConfig, AuthReducer -> core/auth.
//   - User-visible status strings are es-ES (the strings written to the database are
//     byte-identical to the old client).
//   - The context additionally exposes webrtcState, queuePosition and callId so the
//     new UI can render the degraded-connection chip and the queue badge.
import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import VideoContext from './VideoContext'
import { globals } from './globals'
import { getAuthEmail, getAuthUser, getLoggedInUser } from './auth'
import { socket, readyTheSocketConnection } from './socket'
import {
  insertCallRecordIntoFirestore,
  updateCallStatus,
  updateCallStatusByUserId,
} from './callStatus'
import { v4 as uuidv4 } from 'uuid'
import { t } from '../i18n'
import { database, authReady } from './firebase'
import { child, off, onValue, ref } from 'firebase/database'
import { httpRequest } from './http'
import {
  applyRelayPolicy,
  closeUserPeerConnections,
  getPeerConnection,
  onIceCandidateOffer,
  processPendingCandidates,
  sendOfferToRemoteUser,
  setPeerConnection,
  constraints,
  isThereVideoInPeer,
} from './peer.service'
import { createLog } from './logger'
import toast from 'react-hot-toast'

let pingInterval

// PURE LOGGING LISTENERS, ATTACHED ONCE PER PAGE LOAD SO THEY DO NOT PILE UP ON EVERY CALL.
// SINCE SOCKET.IO V3 THE RECONNECTION EVENTS FIRE ON THE MANAGER (socket.io), NOT ON THE SOCKET.
let socketLifecycleLogsAttached = false
const attachSocketLifecycleLogs = () => {
	if (socketLifecycleLogsAttached) {
		return
	}
	socketLifecycleLogsAttached = true

	socket.io.on('reconnect_attempt', (attemptNumber) => {
		createLog('webrtc_debug', `Reconnection attempt #${attemptNumber}`)
	})

	socket.io.on('reconnect_failed', () => {
		createLog('webrtc_debug', 'Reconnection failed')
	})

	socket.io.on('reconnect', (attemptNumber) => {
		createLog('webrtc_debug', `Successfully reconnected to the server after ${attemptNumber} attempt(s)`)
	})

	socket.on('connect', () => {
		createLog('webrtc_debug', 'Socket connected with main server')
	})

	socket.on('connect_error', (err) => {
		createLog('webrtc_debug', `Socket connect error: ${err.message}`)
	})
}

const VideoState = ({ children, isCallStatus }) => {
	// GET THE CURRENT LOCATION AND STATE FROM REACT ROUTER
	const location = useLocation()
	const { state } = location

	const navigate = useNavigate()
	const isDirectCallEnabled = useRef(isCallStatus == 'IN' ? globals.enabledDirectCall == 1 : state?.enabledDirectCall == 1 ? true : false)
	const callerCode = useRef(isCallStatus == 'IN' ? 'Staff' : 'Caller')

	// PER-QR SETTING DECIDING WHETHER THE CALLBACK CONTACT FORM IS OFFERED WHEN NO AGENT
	// ANSWERS (#60). WHEN THE SCAN TOKEN PREDATES THE BACKEND FIELD (state?.showCallbackForm
	// IS undefined/null) FALL BACK TO THE OLD KIOSK/DIRECT-CALL INFERENCE: A DIRECT-CALL
	// (KEYBOARD-LESS) QR HIDES THE FORM, ANY OTHER QR SHOWS IT.
	const showCallbackForm = useRef(
		state?.showCallbackForm !== undefined && state?.showCallbackForm !== null
			? state.showCallbackForm == 1
			: !isDirectCallEnabled.current
	)

	// SET IT TO FALSE BECAUSE BY DEFAULT, ALL CALLS ARE AUDIO CALLS. EVEN THE VIDEO CALLS WILL START AS AUDIO, BUT
	// LATER THEY WILL BE CONVERTED INTO VIDEO CALL.
	const isItVideoCall = useRef(isDirectCallEnabled.current)
	const [callType, setCallType] = useState(isItVideoCall.current ? 'video' : 'audio')

	// THE GUEST CALLER IS ANONYMOUS (BOTH NULL); THE STAFF SOFTPHONE POPULATES core/auth.
	const email = getAuthEmail()
	const user = getAuthUser()

	const [call, setCall] = useState({})
	const [callAccepted, setCallAccepted] = useState(false)
	const [stream, setStream] = useState(null)
	const [remoteUsername, setRemoteUsername] = useState('')
	const [callEnded, setCallEnded] = useState(false)
	const [me, setMe] = useState('')
	const [username, setUsername] = useState('')
	const [remoteUserSocketId, setRemoteUserSocketId] = useState(null)
	const [startCall, setStartCall] = useState(false)
	const [callId, setCallId] = useState(null)
	const [callStartTime, setCallstarttime] = useState(null)
	const [isMyVideoOn, setIsMyVideoOn] = useState(isItVideoCall.current)
	const [userVdoStatus, setUserVdoStatus] = useState(false)
	const [myMicStatus, setMyMicStatus] = useState(true)
	const [userMicStatus, setUserMicStatus] = useState()
	const [staffUnavailable, setStaffUnavailable] = useState(false)
	const [message, setMessage] = useState('')
	const [callRejected, setcallRejected] = useState()
	const [isSafariBrowser, setisSafariBrowser] = useState(false)
	const [webrtcState, setwebrtcState] = useState('Connecting...')
	const [callStatus, setCallStatus] = useState('')
	const [queuePosition, setQueuePosition] = useState(null)
	const [isCallConnected, setIsCallConnected] = useState(false)
	const [frontCam, setfrontCam] = useState(true)
	const [phoneSpeaker, setPhoneSpeaker] = useState('phone')
	const [supportsMultipleCameras, setSupportsMultipleCameras] = useState(false)
	const [isHideOptionButtons, setHideOptionButtons] = useState(false)

	const myVideo = useRef()
	const userVideo = useRef()
	const userAudio = useRef()
	const connectionRef = useRef()
	const call_detail_id = useRef()
	const localStream = useRef(null)
	const isFirstCall = useRef(null)
	const callInitialized = useRef(false)
	// TRUE ONCE THIS CALLER HAS EMITTED dial-call FOR THIS CALL ATTEMPT. GUARDS AGAINST THE
	// RE-DIAL STORM (#52): a socket reconnect while queued/ringing must NOT emit a fresh
	// dial-call (that creates a brand-new call and re-rings every agent). One mount = one
	// call attempt, so this is never reset within a mount.
	const hasDialedRef = useRef(false)

	// LIVE COPY OF THE REMOTE SOCKET ID: THE ICE-RESTART TIMERS RUN OUTSIDE THE RENDER CYCLE,
	// SO READING THE STATE VARIABLE THERE WOULD GIVE A STALE (POSSIBLY DEAD) SOCKET ID.
	const remoteUserSocketIdRef = useRef(null)
	const iceRestartAttempts = useRef(0)
	const iceRestartTimer = useRef(null)
	const onSocketConnectRef = useRef(null)
	const webrtcRecoveryTimer = useRef(null)
	// ONE-SHOT WATCHDOG FOR A MEDIA PATH THAT NEVER LEAVES "checking"/"connecting" (NO
	// candidate PAIR FORMS), AND A LATCH SO WE ONLY ESCALATE TO RELAY ONCE PER CALL.
	const mediaConnectTimer = useRef(null)
	const relayEscalated = useRef(false)
	// ONE-SHOT SAMPLE OF THE MEDIA PATH ONCE THE CALL IS UP (SEE logMediaPathSnapshot).
	const mediaPathSnapshotTimer = useRef(null)

	// HOW LONG A BROKEN WEBRTC CONNECTION MAY TRY TO RECOVER (VIA ICE RESTARTS) BEFORE THE
	// CALL IS ENDED. COVERS THE FULL ICE-RESTART BACKOFF SCHEDULE.
	const WEBRTC_RECOVERY_DEADLINE_MS = 30000

	// HOW LONG TO WAIT FOR THE MEDIA PATH TO CONNECT BEFORE ESCALATING. A CONNECTION THAT
	// NEVER LEAVES "checking" (e.g. PHONE-TO-PHONE BEHIND SYMMETRIC NAT, WHERE ONLY A RELAY
	// PAIR WORKS) WOULD OTHERWISE SIT ON "Connecting" UNTIL THE BROWSER'S OWN LONG ICE
	// TIMEOUT. ON TIMEOUT WE FORCE A TURN-RELAY-ONLY ICE RESTART.
	const MEDIA_CONNECT_WATCHDOG_MS = 12000

	// AUDIO CALL SECONDS
	const [sec, setSec] = useState(0)
	const [isRunning, setRunning] = useState(false)

	// TIMEOUT IF CALL NOT RECEIVED
	const remainingTimeRef = useRef(90000) // INITIAL TIMER: 90 SECONDS (90000MS)
	const timerRef = useRef(null)
	const [staffTimeOut, setstaffTimeOut] = useState(false)

	const handleStartButton = () => {
		setRunning(true)
	}
	const handleResetButton = () => {
		setSec(0)
		setRunning(false)
	}

	// THIS HOOK TO USE THE CALL TIMER START WHEN CALL IS CONNECTED.
	useEffect(() => {
		if (isRunning) {
			const intervalId = setTimeout(() => {
				setSec((prevCount) => prevCount + 1)
			}, 1000)

			return () => {
				clearTimeout(intervalId)
			}
		}
	}, [sec, isRunning])

	// SOCKET EVENTS
	useEffect(() => {
		const initialize = async () => {
			try {
				await readyTheSocketConnection()
				// FOR SAFARI
				let userAgent = navigator.userAgent
				if (userAgent.match(/safari/i)) {
					setisSafariBrowser(true)
				}

				startCallOutTimer(remainingTimeRef.current) // 90 SEC TIMER

				if (isDirectCallEnabled.current && isCallStatus === 'OUT') {
					setHideOptionButtons(true)
				}

				// SUBSCRIBE THE SOCKET EVENTS.
				subscribeSocketEvent()

				window.addEventListener('beforeunload', () => {
					socket.disconnect()
				})
			} catch (e) {
				console.log('Error in VideoState useEffect', e)
				let reason = callerCode.current + ' have error on peer connections. Please try again.'
				leaveCall(reason, true)
			}
		}
		initialize()

		return () => {
			window.addEventListener('beforeunload', () => {
				socket.disconnect()
			})
			// TERMINATE THE CALL CONNECTION.
			terminateCallConnection()
			clearTimeout(timerRef.current)
			clearInterval(pingInterval)
		}
	}, [])

	const handleConnectionStateChangeEvent = (event, peerConnection) => {
		createLog('webrtc_debug', 'connection state change event: ', peerConnection.connectionState)
		if (peerConnection.connectionState === 'connected') {
			setIsCallConnected(true)
			clearMediaConnectWatchdog()
			scheduleMediaPathSnapshot(peerConnection)
		}
		webrtcState !== peerConnection.connectionState && setwebrtcState(peerConnection.connectionState)
	}

	// HOW LONG AFTER CONNECTING WE SAMPLE THE MEDIA PATH. LONG ENOUGH FOR REAL BYTE COUNTERS TO
	// MOVE, SHORT ENOUGH TO BE THERE WHEN SOMEBODY REPORTS "I CANNOT HEAR/SEE THE OTHER SIDE".
	const MEDIA_PATH_SNAPSHOT_MS = 8000

	const scheduleMediaPathSnapshot = (peerConnection) => {
		if (mediaPathSnapshotTimer.current) {
			return // ONE SAMPLE PER CALL IS ENOUGH.
		}
		mediaPathSnapshotTimer.current = setTimeout(() => {
			mediaPathSnapshotTimer.current = null
			logMediaPathSnapshot(peerConnection).then()
		}, MEDIA_PATH_SNAPSHOT_MS)
	}

	// WHICH PATH THE MEDIA TOOK AND WHETHER EACH TRACK IS ACTUALLY MOVING BYTES. A CALL WHERE ONE
	// DIRECTION IS SILENT LOOKS EXACTLY LIKE A HEALTHY ONE FROM EVERY STATE MACHINE WE ALREADY
	// LOG: THE PEER CONNECTION IS "connected" AND THE TRACKS EXIST. THIS IS THE ONLY PLACE THAT
	// TELLS A NEGOTIATION PROBLEM (NO TRACK AT ALL) FROM A TRANSPORT ONE (TRACK WITH ZERO BYTES)
	// WITHOUT ASKING THE AGENT TO OPEN THE BROWSER'S INTERNAL WEBRTC PAGE. COUNTERS AND CANDIDATE
	// TYPES ONLY, NEVER MEDIA CONTENT.
	const logMediaPathSnapshot = async (peerConnection) => {
		try {
			if (!peerConnection || peerConnection.signalingState === 'closed') {
				return
			}
			const stats = await peerConnection.getStats()
			const byId = new Map()
			stats.forEach((report) => byId.set(report.id, report))

			let pair = null
			stats.forEach((report) => {
				// Chrome marks the live pair as `selected`; other browsers only link it from the transport.
				if (report.type === 'candidate-pair' && (report.selected || report.nominated) && report.state === 'succeeded') {
					pair = report
				}
				if (report.type === 'transport' && report.selectedCandidatePairId) {
					pair = byId.get(report.selectedCandidatePairId) || pair
				}
			})

			const local = pair ? byId.get(pair.localCandidateId) : null
			const remote = pair ? byId.get(pair.remoteCandidateId) : null
			const path = pair
				? `${local?.candidateType || '?'}/${local?.protocol || '?'} -> ${remote?.candidateType || '?'}/${remote?.protocol || '?'}`
				: 'no selected candidate pair'

			const flows = []
			stats.forEach((report) => {
				if (report.type === 'inbound-rtp' && !report.isRemote) {
					flows.push(`in ${report.kind}: ${report.bytesReceived || 0}B packets=${report.packetsReceived || 0} lost=${report.packetsLost || 0}`)
				}
				if (report.type === 'outbound-rtp' && !report.isRemote) {
					flows.push(`out ${report.kind}: ${report.bytesSent || 0}B packets=${report.packetsSent || 0}`)
				}
			})

			createLog('webrtc_debug', `media path ${path} | ${flows.join(' | ') || 'no rtp flows'}`)
		} catch (e) {
			createLog('webrtc_debug', 'could not sample the media path', 'warn', e?.name)
		}
	}

	// HOW MANY TIMES WE TRY TO REBUILD THE MEDIA PATH BEFORE GIVING UP, AND THE BASE DELAY OF
	// THE EXPONENTIAL BACKOFF BETWEEN ATTEMPTS. "disconnected" OFTEN SELF-HEALS, SO THE FIRST
	// ATTEMPT ALREADY WAITS ONE BASE DELAY; "failed" SKIPS THE WAIT.
	const MAX_ICE_RESTART_ATTEMPTS = 5
	const ICE_RESTART_BASE_DELAY = 2000

	// SCHEDULE AN ICE RESTART. THE RANDOM JITTER ALSO DESYNCHRONIZES BOTH PEERS SO THEIR
	// RESTART OFFERS DO NOT CROSS EACH OTHER (GLARE) WHEN BOTH DETECT THE FAILURE AT ONCE.
	// force SENDS THE RESTART OFFER EVEN IF MEDIA IS STILL HEALTHY: AFTER A SOCKET RECONNECT
	// THE REMOTE PEER MUST LEARN OUR NEW SOCKET ID THROUGH THE RENEGOTIATION.
	const scheduleIceRestart = (reason, immediate = false, force = false) => {
		if (iceRestartTimer.current) {
			return // A RESTART IS ALREADY SCHEDULED.
		}
		if (iceRestartAttempts.current >= MAX_ICE_RESTART_ATTEMPTS) {
			createLog('webrtc_debug', 'ICE restart attempts exhausted, giving up', 'error', reason)
			return
		}
		const backoff = immediate ? 0 : ICE_RESTART_BASE_DELAY * Math.pow(2, iceRestartAttempts.current)
		const delay = backoff + Math.floor(Math.random() * 500)
		createLog('webrtc_debug', `scheduling ICE restart in ${delay}ms`, reason)
		iceRestartTimer.current = setTimeout(() => performIceRestart(reason, force), delay)
	}

	// SEND A REAL ICE-RESTART OFFER (restartIce + createOffer WITH iceRestart) TO THE REMOTE PEER.
	const performIceRestart = (reason, force = false) => {
		iceRestartTimer.current = null
		const peerConnection = getPeerConnection()

		// THE CALL MAY HAVE ENDED OR THE PATH MAY HAVE SELF-HEALED WHILE WE WAITED.
		if (!peerConnection || peerConnection.signalingState === 'closed' || !call_detail_id.current) {
			createLog('webrtc_debug', 'ICE restart cancelled: no active call/peer connection', reason)
			return
		}
		if (!force && ['connected', 'completed'].includes(peerConnection.iceConnectionState)) {
			createLog('webrtc_debug', 'ICE recovered on its own, restart not needed', reason)
			iceRestartAttempts.current = 0
			return
		}

		// WITHOUT SIGNALING THE OFFER CANNOT REACH THE REMOTE PEER: WAIT, THE SOCKET connect
		// HANDLER WILL RESCHEDULE THE RESTART AS SOON AS THE CONNECTION IS BACK.
		if (!socket.connected) {
			createLog('webrtc_debug', 'ICE restart deferred: socket is offline', reason)
			return
		}

		iceRestartAttempts.current++
		createLog('webrtc_debug', `performing ICE restart (attempt ${iceRestartAttempts.current}/${MAX_ICE_RESTART_ATTEMPTS})`, reason)

		// restartIce MAKES THE BROWSER DISCARD THE CURRENT CANDIDATE PAIRS; THE EXPLICIT
		// iceRestart OFFER COVERS BROWSERS WHERE THE METHOD IS NOT AVAILABLE.
		if (typeof peerConnection.restartIce === 'function') {
			peerConnection.restartIce()
		}
		sendOfferToRemoteUser(remoteUserSocketIdRef.current || remoteUserSocketId, remoteUsername, me, location, true).then()

		// IF THIS ATTEMPT DOES NOT BRING THE CONNECTION BACK, TRY AGAIN WITH A LARGER BACKOFF.
		scheduleIceRestart(reason + ' (retry)')
	}

	// ARM A ONE-SHOT WATCHDOG WHEN A FRESH PEER CONNECTION STARTS NEGOTIATING. IF THE MEDIA
	// PATH HAS NOT CONNECTED BY THE DEADLINE, ESCALATE TO A TURN-RELAY-ONLY ICE RESTART.
	const armMediaConnectWatchdog = () => {
		if (mediaConnectTimer.current) {
			return // ALREADY ARMED FOR THIS CALL.
		}
		createLog('webrtc_debug', `arming media-connect watchdog (${MEDIA_CONNECT_WATCHDOG_MS}ms)`)
		mediaConnectTimer.current = setTimeout(onMediaConnectWatchdog, MEDIA_CONNECT_WATCHDOG_MS)
	}

	const clearMediaConnectWatchdog = () => {
		if (mediaConnectTimer.current) {
			clearTimeout(mediaConnectTimer.current)
			mediaConnectTimer.current = null
		}
	}

	// THE MEDIA PATH DID NOT CONNECT IN TIME. FORCE RELAY AND TRIGGER AN ICE RESTART SO BOTH
	// PEERS GATHER FRESH RELAY CANDIDATES (THE PAIR THAT TRAVERSES SYMMETRIC NAT). RUNS ONCE
	// PER CALL; IF RELAY ALSO FAILS, THE EXISTING failed/disconnected RECOVERY (+30s DEADLINE)
	// TAKES OVER.
	const onMediaConnectWatchdog = () => {
		mediaConnectTimer.current = null
		const peerConnection = getPeerConnection()

		// THE CALL MAY HAVE ENDED OR THE PATH MAY HAVE CONNECTED WHILE WE WAITED.
		if (!peerConnection || peerConnection.signalingState === 'closed' || !call_detail_id.current) {
			return
		}
		if (['connected', 'completed'].includes(peerConnection.iceConnectionState) || peerConnection.connectionState === 'connected') {
			return
		}
		if (relayEscalated.current) {
			return // ALREADY ESCALATED ONCE; LET THE STANDARD RECOVERY HANDLE THE REST.
		}

		relayEscalated.current = true
		createLog('webrtc_debug', 'warn', 'media did not connect in time; forcing TURN relay and restarting ICE')
		applyRelayPolicy(peerConnection)
		scheduleIceRestart('media connect watchdog', true, true)
	}

	const handleIceConnectionStateChangeEvent = (event) => {
		const iceState = event.target.iceConnectionState
		createLog('webrtc_debug', 'ice connection state change event: ', iceState)

		if (iceState === 'connected' || iceState === 'completed') {
			// THE MEDIA PATH IS HEALTHY (AGAIN): RESET THE RESTART STATE.
			if (iceRestartAttempts.current > 0) {
				createLog('webrtc_debug', 'ICE connection recovered after restart')
			}
			iceRestartAttempts.current = 0
			if (iceRestartTimer.current) {
				clearTimeout(iceRestartTimer.current)
				iceRestartTimer.current = null
			}
			clearMediaConnectWatchdog()
		} else if (iceState === 'failed') {
			createLog('webrtc_debug', 'warn', 'ICE connection failed. Restarting ICE...')
			scheduleIceRestart('ice failed', true)
		} else if (iceState === 'disconnected') {
			// "disconnected" IS OFTEN TRANSIENT: GIVE IT A GRACE PERIOD BEFORE RESTARTING.
			createLog('webrtc_debug', 'warn', 'ICE connection unstable. Scheduling ICE restart...')
			scheduleIceRestart('ice disconnected')
		}
	}

	const handleOnTrackEvent = (event, remote_user_id) => {
		// IF EVENT IS AUDIO THEN USE AUDIO ELEMENT.
		const element = event.track.kind === 'audio' ? userAudio : userVideo

		// HERE WE GOT REMOTE STREAM
		createLog('webrtc_debug', 'on track event fired.')
		handleStartButton()
		let remoteUserStream = new MediaStream([event.track])

		// IF ELEMENT NOT FOUND THEN TRY TO GET IT BY ID.
		if (!element.current) {
			createLog('webrtc_debug', 'element was not found. trying to get it by id', 'warn')
			element.current = document.getElementById(event.track.kind === 'audio' ? 'remoteAudio' : 'remoteVideo')
		}
		element.current.srcObject = remoteUserStream

		// play() REJECTS ASYNCHRONOUSLY (E.G. THE BROWSER'S AUTOPLAY POLICY BLOCKS IT), SO THE
		// try/catch AROUND IT NEVER SAW ANYTHING: A REMOTE TRACK THAT ARRIVED BUT NEVER PLAYED —
		// ONE SIDE HEARING NOTHING WHILE EVERY STATE LOOKS HEALTHY — LEFT NO TRACE AT ALL.
		try {
			const started = element.current.play()
			if (started && typeof started.catch === 'function') {
				started.catch((e) => {
					createLog('webrtc_debug', `remote ${event.track.kind} did not start playing`, 'error', e?.name || e)
				})
			}
		} catch (e) {
			createLog('webrtc_debug', 'error in playing the remote stream', 'error', e)
		}
	}

	// FUNCTION TO START THE TIMER
	const startCallOutTimer = (time) => {
		clearTimeout(timerRef.current) // CLEAR ANY EXISTING TIMER
		timerRef.current = setTimeout(() => {
			// IF CALLER THEN UPDATE CALL STATUS TO MISSED.
			// READ THE ID ONCE: terminateCallConnection() clears the ref, and it can run while the
			// status update below is still in flight, which left the Firestore copy being asked for
			// with a null id ("Invalid call_id") every time both raced.
			const missedCallId = call_detail_id.current
			if (missedCallId) {
				// UPDATE CALL STATUS TO MISSED
				updateCallStatus(missedCallId, 'MISS').then(async () => {
					// INSERT CALL DETAIL IN FIRESTORE.
					await insertCallRecordIntoFirestore(missedCallId)
				})
				socket.emit('endCall', remoteUserSocketId)

				//  NAVIGATE TO STAFF NOT AVAILABLE PAGE.
				navigate('/staffnotavailable', {
					state: {
						userinfo: state?.userInfo,
						reason: 'MISS',
						call_id: missedCallId,
						showCallbackForm: showCallbackForm.current,
						// A DIRECT-CALL QR IS THE KEYBOARD-LESS PUBLIC SCREEN. THE NEXT SCREEN USES IT
						// TO OFFER A CODE FOR CONTINUING ON THE GUEST'S OWN PHONE (#61).
						enabledDirectCall: isDirectCallEnabled.current ? 1 : 0,
					},
				})
				return
			}
			setstaffTimeOut(true)
		}, time)
	}

	const handleMessages = (data, callback) => {
		switch (data.command) {
			case 'start_call':
				initiateCall(data)
				callback('call will be started.')
				break
		}
	}

	const handleMessagesResponse = (response) => {
		console.log('Response for send message received from remote user', response)
	}

	// SOCKET EVENTS
	const subscribeSocketEvent = () => {
		createLog('webrtc_debug', 'subscribing to socket events...')

		const onSocketConnect = () => {
			createLog('webrtc_debug', 'socket connected with id:', socket.id)
			setMe(socket.id)

			// ALWAYS SET THE FLAG WHEN SOCKET CONNECTS
			callInitialized.current = true

			// IF A CALL IS ALREADY IN PROGRESS, THIS connect IS A RECONNECTION: REJOIN THE SAME
			// ROOM (THE SERVER KEEPS THE CALL ALIVE DURING A GRACE PERIOD) INSTEAD OF DIALING A
			// BRAND NEW CALL, WHICH RANG EVERY AGENT AGAIN AND ORPHANED THE ORIGINAL CALL.
			if (call_detail_id.current && globals.localUser?.room_id) {
				createLog('webrtc_debug', 'socket reconnected with a call in progress: rejoining room', globals.localUser.room_id)
				globals.localUser.socket_id = socket.id
				globals.localUser.reconnected = true
				socket.emit('join_room', globals.localUser)

				// RENEGOTIATE: THE ICE-RESTART OFFER CARRIES OUR NEW SOCKET ID TO THE REMOTE
				// PEER (SIGNALING IS ADDRESSED BY SOCKET ID) AND REBUILDS MEDIA IF IT BROKE.
				if (getPeerConnection()) {
					iceRestartAttempts.current = 0
					scheduleIceRestart('socket reconnected', false, true)
				}
				return
			}

			// STAFF SIDE OF AN ONGOING CALL (IT HAS NO ROOM TO REJOIN): SAME RENEGOTIATION SO THE
			// CALLER LEARNS THIS PEER'S NEW SOCKET ID AND MEDIA RECOVERS.
			if (call_detail_id.current && getPeerConnection()) {
				createLog('webrtc_debug', 'socket reconnected with a call in progress: renegotiating')
				iceRestartAttempts.current = 0
				scheduleIceRestart('socket reconnected', false, true)
				return
			}

			// IF THIS IS A CALLER THEN START THE CALL.
			if (isCallStatus === 'OUT' || state?.callingStatus === 'O') {
				// STORM GUARD (#52): only dial ONCE per call attempt. If we have already dialled,
				// this connect is a reconnect with no room to rejoin yet (e.g. still queued, the
				// call id has not arrived). Re-emitting dial-call here is what caused the re-dial
				// storm (a new call + every agent re-rung on each reconnect, amplified by the
				// long-polling reconnect churn from #47). The getCallIdFromServer listener stays
				// registered across reconnects, so a late call id is still handled. Do nothing.
				if (hasDialedRef.current) {
					createLog('webrtc_debug', 'reconnect while dialing/queued: not re-dialing (storm guard)')
					return
				}

				createLog('webrtc_debug', 'dial call', 'next: get Call IdFrom Server')

				// SEND A CALL REQUEST TO SERVER. SERVER WILL SEND CALL NOTIFICATION TO STAFF.
				try {
					socket.emit('dial-call', state?.staffToCall, state?.userInfo, state)
					hasDialedRef.current = true
					createLog('webrtc_debug', 'dial-call socket emit sent successfully')
				} catch (error) {
					createLog('webrtc_debug', 'ERROR in socket.emit dial-call:', error)
					console.error('Socket emit error:', error)
				}

				// SET UP THE CALLBACK FOR GETTING CALL ID
				socket.on('getCallIdFromServer', getCallIdFromServer)
			} else {
				// THIS IS CALL RECEIVER. SO THE CALL ID WILL BE AVAILABLE FROM GLOBALS.
				createLog('webrtc_debug', 'call receiver, got the call id', globals.call_id)
				setCallId(globals.call_id) // SET STATE.
				call_detail_id.current = globals.call_id
			}
		}

		// ALWAYS SUBSCRIBE TO connect, EVEN IF THE SOCKET IS ALREADY UP: A MID-CALL RECONNECTION
		// FIRES connect AGAIN AND MUST RUN THE RESUME LOGIC ABOVE (BEFORE, AN ALREADY-CONNECTED
		// SOCKET NEVER SUBSCRIBED AND THE CALL COULD NOT BE RESUMED AFTER A NETWORK DROP).
		// DETACH ANY PREVIOUS connect HANDLER FIRST: ON A RAPID CALL TRANSFER / REMOUNT THIS CAN
		// RUN AGAIN BEFORE THE OLD ONE WAS CLEANED UP, AND A DUPLICATE HANDLER WOULD FIRE TWICE
		// ON THE NEXT RECONNECT (GHOST RE-DIAL / DOUBLE ROOM REJOIN).
		if (onSocketConnectRef.current) {
			socket.off('connect', onSocketConnectRef.current)
		}
		onSocketConnectRef.current = onSocketConnect
		socket.on('connect', onSocketConnect)
		if (socket.connected) {
			createLog('webrtc_debug', 'Socket already connected, calling onSocketConnect')
			onSocketConnect()
		} else {
			createLog('webrtc_debug', 'Socket not connected, waiting for connect event')
		}

		// REGISTER OTHER LISTENERS
		socket.on('send_message', handleMessages)
		socket.on('send_message_response', handleMessagesResponse)

		// THIS EVENT EMITTED WHEN NO STAFF IS AVAILABLE FOR OUTGOING-CALL.
		socket.on('staff-unavailable', handleStaffUnavailable)

		// EVENTS EMITTED WHILE EVERY AGENT IS BUSY AND THE CALLER WAITS IN THE QUEUE: THE SERVER
		// WILL DIAL THIS CALLER AUTOMATICALLY WHEN AN AGENT FREES UP.
		socket.on('call-queued', handleCallQueued)
		socket.on('queue-update', handleCallQueued)

		// THIS EVENT EMITTED WHEN STAFF ACCEPTED CALL FOR OUTGOING-CALL.
		socket.on('staff-details', handleStaffDetail)

		socket.on('offer', handleOffer)

		// THIS EVENT WILL BE TRIGGERED WHEN THERE IS ICE CANDIDATE INFORMATION TO BE SHARED FOR BOTH.
		socket.on('offer-candidate', onIceCandidateOffer)

		// EVENT EMITTED WHEN ANYONE CHANGES THEIR CAMERA AND MIC STATUS FOR BOTH.
		socket.on('updateUserMedia', handleUserMediaUpdate)

		// EVENT EMITTED WHEN STAFF ACCEPT CALL AND EMITTED ANSWER SDP FOR OUTGOING-CALL.
		socket.on('answer', handleAnswer)

		// EVENT EMITTED WHEN CALL ENDED FOR BOTH.
		socket.on('endCall', handleEndCall)
		socket.on('errorOnCall', handleErrorOnCall)

		socket.on('disconnect', function () {
			createLog('webrtc_debug', 'Socket disconnected, resetting callInitialized flag')
			callInitialized.current = false
		})

		attachSocketLifecycleLogs()

		socket.on('timerIncrement', () => {
			// QUEUE MOVED: RESTART THE NO-ANSWER TIMER WITH THE REMAINING TIME.
			const newRemainingTime = remainingTimeRef.current
			remainingTimeRef.current = newRemainingTime
			startCallOutTimer(newRemainingTime)
		})

		socket.on('pong', () => {
			console.log('Pong received')
		})
	}

	// WHEN CALL_ID IS AVAILABLE, THEN SUBSCRIBE TO THE CALL DETAIL IN THE REALTIME DATABASE.
	// THIS WILL BE USED TO LISTEN FOR CHANGES IN THE CALL STATUS.
	useEffect(() => {
		// THIS SHALL ONLY WORK FOR CALLER.
		if (callId && isCallStatus === 'OUT') {
			// REFERENCE TO 'CALL' NODE IN THE DATABASE
			const databaseRef = ref(database, 'call')
			const callRealTimeRef = child(databaseRef, callId)

			// LISTEN FOR CHANGES IN 'CALL' NODE, ONCE THE ANONYMOUS AUTH SESSION IS READY (roadmap #84).
			authReady.then(() => {
				onValue(callRealTimeRef, handleCallDetailSnapshot)
			})

			// CLEAN UP SUBSCRIPTION WHEN COMPONENT UNMOUNTS
			return () => {
				off(callRealTimeRef, 'value', handleCallDetailSnapshot)
			}
		}
	}, [callId])

	// HANDLE SNAPSHOT UPDATES OF THE CALL RECORD (STATUS TEXTS, REJECTIONS, ERRORS).
	const handleCallDetailSnapshot = (snapshot) => {
		const data = snapshot.val()

		let default_staff_id = state?.userInfo?.default_staff_id
		let isDefaultStaffInStaffList = data?.isDefaultStaffInStaffList

		// CHECK IF DEFAULT STAFF IS IN THE NOTIFIED STAFF LIST
		let notifiedStaffIds = []
		if (data?.notified_user_id) {
			notifiedStaffIds = Object.keys(data.notified_user_id)
		}

		let isDefaultStaffInNotifiedList = false
		if (default_staff_id && notifiedStaffIds.length > 0) {
			isDefaultStaffInNotifiedList = notifiedStaffIds.some(
				(staffId) =>
					data.notified_user_id[staffId]?.user_id === default_staff_id ||
					staffId === default_staff_id.toString()
			)
		}

		// HANDLE CALL STATUS UPDATES (USER-VISIBLE TEXTS, localised via i18n)
		if (data.callstatus === 'CONNG') {
			setCallStatus(t.statusConnecting)
		} else if (data.callstatus === 'RING') {
			setCallStatus(t.statusCalling)
		} else if (data.callstatus === 'ACCPT') {
			setCallStatus(t.statusWillAssistShortly)
			clearTimeout(timerRef.current)
		}

		// CHECK toHaveError FLAG IS TRUE THEN NAVIGATE TO ERROR PAGE AND SHOW THE ERROR MESSAGE.
		if (data.callstatus === 'ENDED' && data.toHaveError && data.errorFrom != callerCode.current) {
			navigate('/callErrorHandler', { state: { message: data.reason } })
			return
		}

		// CHECK IF THE CALL STATUS IS 'ENDED' THEN TERMINATE THE CALL.
		// (STAFF SIDE RETURNS TO THE STAFF DASHBOARD — ROADMAP #41 ROUTE GROUP.)
		if (data.callstatus === 'ENDED' && isCallStatus === 'IN') {
			navigate('/staff/dashboard')
		}

		// CHECK IF CALL STATUS IS 'MISS' THEN TERMINATE THE CALL.
		if (data.callstatus === 'MISS' && isCallStatus === 'IN') {
			navigate('/staff/dashboard')
		}

		// IF CALL STATUS IS 'REJCT' AND OUTGOING CALLER THEN CHECK IF ALL STAFF ARE REJECTED THEN END THE CALL.
		if (typeof data.rejectedby_user_id !== 'undefined' && isCallStatus === 'OUT') {
			// IF ALL STAFF HAVE REJECTED THE CALL, END THE CALL
			if (data.rejectedby_user_id.length === data.staffids?.length) {
				// END THE CALL IF ALL STAFF REJECTED THE CALL. ALSO END WHEN THERE IS NO DEFAULT
				// STAFF CONFIGURED AT ALL (!default_staff_id): WITHOUT THIS THE CALLER KEPT RINGING
				// FOREVER ON A QR THAT HAS NO DEFAULT STAFF (#52). WHEN A DEFAULT STAFF EXISTS THE
				// BEHAVIOUR IS UNCHANGED — THE CALL STILL WAITS FOR / FORWARDS TO THAT DEFAULT.
				if (isDefaultStaffInStaffList || !default_staff_id) {
					handleEndCall(true, callId, data.reason || 'All staff rejected the call')
					return
				}
			}
		}

		// IF DEFAULT STAFF RECEIVED THE SEPARATE NOTIFICATION THEN REJECT THE CALL OR MISS THE CALL, THEN END THE CALL.
		// FIRSTLY, WE CHECKED DEFAULT STAFF IS NOT IN THE STAFF LIST WHO RECEIVED THE CALL.
		if (!isDefaultStaffInNotifiedList && default_staff_id) {
			const defaultStaffNotified = data?.defaultstaffnotified

			if (typeof defaultStaffNotified !== 'undefined' && typeof defaultStaffNotified.callstatus !== 'undefined') {
				// IF DEFAULT STAFF REJECTED THE CALL THEN END THE CALL.
				if (
					defaultStaffNotified.callstatus === 'REJTD' ||
					(defaultStaffNotified.callstatuses && defaultStaffNotified.callstatuses.some((status) => status.indexOf('REJTD') !== -1))
				) {
					handleEndCall(true, callId, 'Default staff rejected the call')
					return
				}

				// IF DEFAULT STAFF MISSED THE CALL THEN END THE CALL.
				if (
					defaultStaffNotified.callstatus === 'MISS' ||
					(defaultStaffNotified.callstatuses && defaultStaffNotified.callstatuses.some((status) => status.indexOf('MISS') !== -1))
				) {
					handleEndCall(true, callId, 'Default staff missed the call')
					return
				}
			}
		}

		// CHECK THE notified_user_id MAP. IF ALL NOTIFIED STAFF HAVE MISSED OR REJECTED THE CALL THEN END THE CALL.
		if (data.notified_user_id && Object.keys(data.notified_user_id).length > 0 && isCallStatus === 'OUT') {
			let allStaffMissedOrRejected = true

			Object.keys(data.notified_user_id).forEach((userId) => {
				let userData = data.notified_user_id[userId]

				if (userData) {
					if (userData.callstatuses && Array.isArray(userData.callstatuses)) {
						let hasValidStatus = userData.callstatuses.some((status) => status !== 'MISS' && status !== 'REJTD' && status !== 'ERR')

						if (hasValidStatus) {
							allStaffMissedOrRejected = false
						}
					}
				}
			})

			// IF ALL STAFF HAVE MISSED OR REJECTED THE CALL, END THE CALL
			if (allStaffMissedOrRejected && Object.keys(data.notified_user_id).length > 0) {
				// END THE CALL IF ALL STAFF REJECTED THE CALL — OR IF THERE IS NO DEFAULT STAFF
				// CONFIGURED (!default_staff_id), WHICH OTHERWISE LEFT THE CALLER RINGING (#52).
				// WITH A DEFAULT STAFF PRESENT THE BEHAVIOUR IS UNCHANGED.
				if (isDefaultStaffInNotifiedList || isDefaultStaffInStaffList || !default_staff_id) {
					handleEndCall(true, callId, 'All staff missed or rejected the call')
					return
				}
			}
		}
	}

	// SET CALL DETAIL ID ON STATE
	const getCallIdFromServer = (data) => {
		createLog('webrtc_debug', 'got the call id', data, 'next: join room')
		setCallId(data) // SET STATE.
		call_detail_id.current = data

		// USER OBJECT OF CURRENT USER TO CONTAIN STATIC FIELDS ONLY.
		globals.localUser = {
			room_id: data,
			origin: 'WEB', // FROM WHERE THIS USER IS JOINING FROM? MOBILE APP OR WEB?
			...state?.userInfo, // CURRENT USER.
			stream_id: stream != null ? stream?.id : uuidv4().toString() + '',
			socket_id: socket && typeof socket?.id !== 'undefined' ? socket.id : null,
		}
		createLog('webrtc_debug', 'emitting join room', 'next: wait for staff to answer. Initiate Call')
		socket.emit('join_room', globals.localUser) // LET'S JOIN THE ROOM.

		// Start ping-pong
		pingInterval = setInterval(() => {
			socket.emit('ping', socket?.id)
		}, 3000)
	}

	// THE SERVER QUEUED THIS CALL BECAUSE EVERY AGENT IS BUSY: KEEP THE CALLER INFORMED WHILE
	// THEY WAIT. WHEN AN AGENT FREES UP THE SERVER DIALS AUTOMATICALLY AND THE NORMAL CALL
	// EVENTS (getCallIdFromServer, ETC.) TAKE OVER THIS STATUS.
	const handleCallQueued = (data) => {
		createLog('webrtc_debug', 'call queued by server', JSON.stringify(data))
		setCallStatus(t.statusEveryoneBusy)
		setQueuePosition(data?.position > 1 ? data.position : null)
	}

	const handleStaffUnavailable = async (data) => {
		// MAKE SURE data IS A VALID OBJECT.
		if (data && typeof data === 'object' && Object.keys(data).length > 0) {
			const { call_id, reason, callstatus } = data

			let fireStoreCall_id = call_id ? call_id : callId

			// IF CALLER THEN NAVIGATE TO STAFF NOT AVAILABLE PAGE.
			if (fireStoreCall_id) {
				navigate('/staffnotavailable', {
					state: {
						userinfo: state?.userInfo,
						reason: reason,
						call_id: fireStoreCall_id,
						showCallbackForm: showCallbackForm.current,
						// A DIRECT-CALL QR IS THE KEYBOARD-LESS PUBLIC SCREEN. THE NEXT SCREEN USES IT
						// TO OFFER A CODE FOR CONTINUING ON THE GUEST'S OWN PHONE (#61).
						enabledDirectCall: isDirectCallEnabled.current ? 1 : 0,
					},
				})
				return
			}
			setStaffUnavailable(true) // STAFF IS NOT AVAILABLE

			setMessage(reason)
		} else {
			console.error('staff unavailable. data is not valid', data)
		}
	}

	// THIS CODE WILL RUN FOR CALLER OF THE CALL.
	const handleStaffDetail = (obj) => {
		createLog('webrtc_debug', 'received staff details', obj, 'next: initiate call will be triggered from receiver side. and then this user will receive offer')
		clearTimeout(timerRef.current) // CLEAR ANY EXISTING TIMER
		let answerCallStaff = obj.answerCallStaff
		setRemoteUsername(answerCallStaff.email || answerCallStaff.full_name)
		setRemoteUserSocketId(answerCallStaff.socketId)
		setQueuePosition(null)
		Object.assign(location.state, { staffs: obj.staffs })
	}

	// POSSIBLE VALUES OF TYPE ARE "audio" | "video" | "both".
	const startStream = async (type) => {
		let audioConstraints = type === 'audio' || type === 'both' ? constraints.audio : false
		let videoConstraints = type === 'video' || type === 'both' ? constraints.video : false
		try {
			const currentStream = await navigator.mediaDevices.getUserMedia({
				audio: audioConstraints,
				video: videoConstraints,
			})
			let peerConnection = startPeerConnection(remoteUserSocketId)

			// IF BOTH AUDIO AND VIDEO ARE REQUIRED THEN GET IT.
			if (type === 'both') {
				localStream.current = currentStream // Replace the current stream
				currentStream.getTracks().forEach((track) => peerConnection.addTrack(track, currentStream))
			} else {
				// IF STREAM ALREADY EXISTS, ADD THE NEW TRACK; OTHERWISE, INITIALIZE A NEW STREAM
				if (!localStream.current) {
					localStream.current = new MediaStream()
				}
				createLog('webrtc_debug', `type is ${type} | ${localStream.current.getAudioTracks().length} | ${localStream.current.getVideoTracks().length}`)
				let track = null

				if (type === 'audio' && localStream.current.getAudioTracks().length === 0) {
					track = currentStream.getAudioTracks()[0]
				}
				if (type === 'video' && localStream.current.getVideoTracks().length === 0) {
					createLog('webrtc_debug', 'extract video track')
					track = currentStream.getVideoTracks()[0]
				}

				// ADD THE TRACK TO THE LOCAL STREAM.
				if (track) {
					localStream.current.addTrack(track)
					peerConnection.addTrack(track, localStream.current)

					// VALIDATE THE VIDEO ELEMENT BEFORE SETTING THE SRC OBJECT.
					if (!isDirectCallEnabled.current && (type === 'both' || type === 'video') && myVideo.current) {
						createLog('webrtc_debug', 'display local video stream')
						myVideo.current.srcObject = new MediaStream([track])
					}
				}
			}
			setStream(localStream.current)
			return true
		} catch (error) {
			// WHY MEDIA ACQUISITION FAILED. `error.name` is the only thing that separates a denied
			// permission from an absent or busy device, and this catch used to discard it: every
			// failure ended the call with the same generic sentence, so the reason that reaches the
			// server log (through errorOnCall) named no cause anybody could act on. It is a browser
			// error class produced by our own getUserMedia call, never guest data.
			const errorName = error?.name || 'UnknownError'
			console.error('Error starting stream:', error)
			createLog('webrtc_debug', `getUserMedia failed for type ${type}`, 'error', errorName)

			const wantsVideo = type === 'both' || type === 'video'
			const hasAudio = localStream.current?.getAudioTracks?.()?.length > 0

			// A CAMERA PROBLEM MUST NOT KILL A CALL THAT CAN STILL BE HELD BY VOICE, whatever the
			// cause (denied, absent or already-in-use camera). Before, only a busy camera on an
			// established call was survivable and everything else dropped the call.
			if (wantsVideo && (isCallConnected || hasAudio)) {
				toast.error(t.cameraActivationFailed)
				setIsMyVideoOn(false)
				setCallType('audio')
				isItVideoCall.current = false
				socket.emit('updateMyMedia', {
					type: 'video',
					currentMediaStatus: false,
					from: remoteUserSocketId,
				})
				return false
			}

			// AT CALL SETUP THERE IS NO STREAM YET: RETRY AUDIO-ONLY INSTEAD OF DROPPING THE CALL.
			// The guest reaches the agent by voice, which is the point of the product; the camera
			// can still be turned on later once the permission or the device is sorted out.
			if (type === 'both') {
				createLog('webrtc_debug', `camera unavailable (${errorName}), falling back to an audio-only call`)
				toast.error(t.cameraActivationFailed)
				setIsMyVideoOn(false)
				setCallType('audio')
				isItVideoCall.current = false
				return await startStream('audio')
			}

			// THE MICROPHONE ITSELF FAILED: THERE IS NO CALL LEFT TO HOLD. The reason never reaches
			// the screen (CallError always shows a generic localised message), so it carries the
			// technical detail for the log.
			leaveCall(`Media unavailable (${type}): ${errorName}`, true)
			return false
		}
	}

	// INITIALIZE THE BOTH AUDIO AND VIDEO STREAM.
	const startVideoCall = async () => {
		try {
			await startStream('both')
			sendOfferToRemoteUser(remoteUserSocketId, remoteUsername, me, location).then()
			createLog('webrtc_debug', 'Added video & audio tracks to PC', 'next: handle negotiation')
		} catch (e) {
			console.error('error in getting video stream', e)
			let reason = callerCode.current + ' video stream not available.'
			leaveCall(reason, true)
		}
	}

	// KEEP THE REF IN SYNC SO TIMERS AND SOCKET CALLBACKS ALWAYS SEE THE CURRENT REMOTE SOCKET ID.
	useEffect(() => {
		remoteUserSocketIdRef.current = remoteUserSocketId
	}, [remoteUserSocketId])

	useEffect(() => {
		// ON THE CALLER SIDE, WHEN THE STAFF USER'S REMOTE ID IS AVAILABLE THEN SEND A COMMAND TO
		// RECEIVER TO START THE CALL.
		if (remoteUserSocketId) {
			createLog('webrtc_debug', `remote user id is: ${remoteUserSocketId} & call direction is: ${isCallStatus}`)
			if (isCallStatus === 'OUT') {
				let postData = {
					to: remoteUserSocketId,
					command: 'start_call',
					socket_id: socket.id,
				}
				createLog('webrtc_debug', 'emit send message with data ', postData)
				socket.emit('send_message', postData)
			} else {
				// THIS IS RECEIVER/STAFF. IF THERE IS A COMMAND TO START_CALL THEN DO IT.
				if (startCall) {
					processStartCall().then()
				}
			}
		}
	}, [remoteUserSocketId, startCall])

	const processStartCall = async () => {
		createLog('webrtc_debug', 'call process started. ', 'next: start ' + (isDirectCallEnabled.current ? 'video' : 'audio') + ' call')
		if (isDirectCallEnabled.current) {
			await startVideoCall()
		} else {
			await startAudioOnlyCall()
		}
	}

	// THIS WILL BE CALLED FROM SOCKET EVENT "send_message" (COMMAND start_call). IT WILL BE
	// TRIGGERED FROM "call-answered" FROM SERVER. THIS WILL BE EXECUTED FOR THE CALLEE/STAFF.
	const initiateCall = (data) => {
		createLog('webrtc_debug', 'initiate call', data, 'next start call process')
		setRemoteUserSocketId(data.socket_id)
		setStartCall(true)

		if (!data.socket_id) {
			console.error("remote user's socket id is not available")
		}
	}

	// FUNCTION TO HANDLE BOTH INCOMING-CALL AND OUTGOING-CALL OFFERS.
	const handleOffer = async (data) => {
		createLog('webrtc_debug', 'got the offer', 'next: send answer to receiver.')
		if (data.signal.type === 'offer') {
			let peerConnection = startPeerConnection(data.from)

			// IF TIMER IS RUNNING THEN CLEAR THE TIMER OUT REFERENCE.
			if (timerRef.current) clearTimeout(timerRef.current)

			// CREATE ANSWER FOR INCOMING CALL.
			try {
				if (peerConnection) {
					try {
						// START CALL.
						createLog('webrtc_debug', 'got offer. now start new call', data.from)

						// IF THIS USER HAS NOT YET STARTED A CALL THEN START THE STREAM FOR THIS USER.
						if (localStream.current === null || typeof localStream.current === 'undefined') {
							isFirstCall.current = true
							if (isDirectCallEnabled.current) {
								await startStream('both')
							} else {
								await startStream('audio')
							}
						} else {
							createLog('webrtc_debug', 'local stream is not null. Dont start anything.')
							isFirstCall.current = false
						}

						// CREATE THE OFFER RTCSessionDescription THEN STORE THE REMOTE DESCRIPTION.
						let offerRemoteUser = new RTCSessionDescription({
							type: 'offer',
							sdp: data.signal.sdp,
						})

						// GLARE: IF BOTH PEERS SENT AN (ICE-RESTART) OFFER AT THE SAME TIME, DROP OUR
						// PENDING LOCAL OFFER AND ANSWER THE REMOTE ONE INSTEAD OF FAILING THE CALL.
						if (peerConnection.signalingState === 'have-local-offer') {
							createLog('webrtc_debug', 'offer glare detected: rolling back local offer')
							// A FAILED ROLLBACK MUST NOT ABORT RENEGOTIATION: SWALLOW IT AND STILL TRY TO
							// APPLY THE REMOTE OFFER, WHICH IS WHAT KEEPS THE CALL ALIVE THROUGH GLARE.
							try {
								await peerConnection.setLocalDescription({ type: 'rollback' })
							} catch (rollbackError) {
								createLog('webrtc_debug', 'rollback failed, continuing with remote offer', 'warn', rollbackError)
							}
						}

						// SET THE REMOTE DESCRIPTION.
						await peerConnection.setRemoteDescription(offerRemoteUser)

						// THE REMOTE PEER MAY HAVE RECONNECTED WITH A NEW SOCKET ID: KEEP OUR POINTER
						// FRESH SO updateMyMedia/endCall/ICE RESTARTS KEEP REACHING IT.
						if (data.from && data.from !== remoteUserSocketIdRef.current) {
							createLog('webrtc_debug', 'remote socket id changed, updating', remoteUserSocketIdRef.current, '->', data.from)
							remoteUserSocketIdRef.current = data.from
							setRemoteUserSocketId(data.from)
						}

						processPendingCandidates(peerConnection)
					} catch (e) {
						createLog('webrtc_debug', 'error in handling offer', 'error', e)
					}

					// TRY TO CREATE THE ANSWER AND SEND IT TO A REMOTE USER.
					try {
						let answer = await peerConnection.createAnswer()
						await peerConnection.setLocalDescription(answer)

						const hasVideoTrack = isThereVideoInPeer(peerConnection)

						let user_email = email
						if (!user_email) {
							// GET CURRENT USER'S PROFILE DATA.
							let loggedUser = await getLoggedInUser()
							user_email = loggedUser?.email
						}
						let userSocketId = me ? me : socket.id

						// SET THE ANSWER OBJECT AND SEND IT TO A REMOTE USER.
						const payload = {
							to: data.from, // SOCKET ID OF REMOTE USER.
							caller: userSocketId, // SOCKET ID OF CURRENT USER.
							signal: { type: 'answer', sdp: answer.sdp }, // SESSION DESCRIPTION PROTOCOL INFORMATION.
							userName: '',
							type: !hasVideoTrack ? 'mic' : 'both',
							myMediaStatus: !hasVideoTrack ? true : [true, true],
							staffs: data.staffs,
							email: user_email,
							callType: hasVideoTrack ? 'video' : 'audio',
						}

						socket.emit('answer', payload) // SEND DATA TO REMOTE USER.
						globals.callStartTime = new Date()
					} catch (e) {
						console.error('webrtc_debug error in creating answer.', e)
					}
				} else {
					console.error('peer not connected')
				}
			} catch (error) {
				console.error('webrtc_debug handle call offer error ', error)
			}
		}

		if (isCallStatus === 'OUT') {
			const { from, name: callerName, signal } = data
			setCall({ isReceivingCall: true, from, name: callerName, signal })
		}
	}

	const handleAnswer = async (data) => {
		setCallAccepted(true)
		setCallstarttime(new Date())

		// IF TIMER IS RUNNING THEN CLEAR THE TIMER OUT REFERENCE.
		if (timerRef.current) clearTimeout(timerRef.current)

		const mediaData = data.data
		createLog('webrtc_debug', 'handle answer ', 'next: exchange offer/answer -> ontrack')
		let peerConnection = getPeerConnection()

		// WITH ICE RESTARTS A LATE/DUPLICATE ANSWER CAN ARRIVE WHEN THERE IS NO PENDING LOCAL
		// OFFER ANY MORE; APPLYING IT WOULD THROW AND COULD BREAK THE HEALTHY CONNECTION.
		if (peerConnection.signalingState === 'stable') {
			createLog('webrtc_debug', 'answer received with no pending offer, ignoring it.')
			return
		}

		try {
			await peerConnection.setRemoteDescription(new RTCSessionDescription(mediaData.signal))
			processPendingCandidates(peerConnection)
		} catch (error) {
			createLog('Error setting remote description', 'error', error)
		}
	}

	const handleEndCall = async (reject, call_id, rejectionDetail) => {
		setcallRejected(reject)
		if (reject) {
			let updateID = call_id || rejectionDetail.call_id

			// INSERT CALL DETAIL IN FIRESTORE.
			await insertCallRecordIntoFirestore(updateID)
			terminateCallConnection()
		} else {
			leaveCall(reject)
		}
	}

	const handleErrorOnCall = async (data) => {
		setcallRejected(data.reject)
		if (data.reject) {
			let updateID = data.call_id

			// INSERT CALL DETAIL IN FIRESTORE.
			await insertCallRecordIntoFirestore(updateID)
			navigate('/callErrorHandler', { state: { message: data.reason } })
			terminateCallConnection()
		}
	}

	// UPDATE THE REMOTE USER MEDIA STATUS (CAMERA/MIC) SHOWN IN THE UI.
	const handleUserMediaUpdate = ({ type, currentMediaStatus }) => {
		if (currentMediaStatus !== null || currentMediaStatus.length !== 0) {
			switch (type) {
				case 'video':
					setUserVdoStatus(currentMediaStatus)

					if (!currentMediaStatus && userVideo.current) {
						// IF VIDEO STATUS IS FALSE THEN STOP THE REMOTE VIDEO TRACK.
						userVideo.current.srcObject = null
					} else if (currentMediaStatus && userVideo.current) {
						// VIDEO STATUS IS TRUE AGAIN: a remote track that was only disabled (not
						// removed) does NOT fire ontrack again, so re-attach the current remote
						// video track from the peer connection. Without this, re-enabling the
						// remote camera after turning it off left the element black.
						const pc = getPeerConnection()
						const remoteVideoTrack = pc
							?.getReceivers?.()
							?.map((r) => r.track)
							?.find((tr) => tr && tr.kind === 'video')
						if (remoteVideoTrack) {
							userVideo.current.srcObject = new MediaStream([remoteVideoTrack])
							try {
								userVideo.current.play()
							} catch (e) {}
						}
					}
					break
				case 'mic':
					setUserMicStatus(currentMediaStatus)
					break
				default:
					setUserMicStatus(currentMediaStatus[0])
					setUserVdoStatus(currentMediaStatus[1])
					break
			}
		}
	}

	// this method is used to mute/unmute our stream
	const updateMic = () => {
		setMyMicStatus((currentStatus) => {
			socket.emit('updateMyMedia', {
				type: 'mic',
				currentMediaStatus: !currentStatus,
				from: remoteUserSocketId,
			})
			stream.getAudioTracks()[0].enabled = !currentStatus
			localStream.current.getAudioTracks()[0].enabled = !currentStatus
			return !currentStatus
		})
	}

	// HANDLE ANY CHANGE IN WEBRTC CONNECTION STATE. POSSIBLE STATES ARE:
	// CONNECTING | CONNECTED | DISCONNECTED | FAILED | CLOSED
	useEffect(() => {
		if (webrtcState === 'connected' && webrtcRecoveryTimer.current) {
			// THE CONNECTION CAME BACK (TYPICALLY THANKS TO AN ICE RESTART): KEEP THE CALL.
			clearTimeout(webrtcRecoveryTimer.current)
			webrtcRecoveryTimer.current = null
			createLog('webrtc_debug', 'webrtc connection recovered, hangup deadline cancelled')
		}

		if (webrtcState == 'disconnected' || webrtcState === 'failed') {
			// DO NOT HANG UP RIGHT AWAY: THE ICE-RESTART LOGIC GETS A RECOVERY WINDOW FIRST.
			// THE CALL ONLY ENDS IF THE CONNECTION IS STILL BROKEN WHEN THE DEADLINE FIRES.
			if (!webrtcRecoveryTimer.current) {
				createLog('webrtc_debug', 'warn', `webrtc connection ${webrtcState}: waiting ${WEBRTC_RECOVERY_DEADLINE_MS}ms for recovery before ending the call`)
				webrtcRecoveryTimer.current = setTimeout(() => {
					webrtcRecoveryTimer.current = null
					const peerConnection = getPeerConnection()
					if (peerConnection && ['connected', 'completed'].includes(peerConnection.iceConnectionState)) {
						return // RECOVERED IN THE MEANTIME.
					}
					console.error('Webrtc connection has ' + webrtcState + ' and did not recover')
					if (isCallStatus === 'OUT') {
						leaveCall('Webrtc connection has ' + webrtcState)
					} else {
						hangupCall()
					}
				}, WEBRTC_RECOVERY_DEADLINE_MS)
			}
		}

		if (webrtcState == 'closed') {
			// A CLOSED PEER CONNECTION NEVER RECOVERS.
			if (isCallStatus === 'OUT') {
				console.error('Webrtc connection has ' + webrtcState)
				leaveCall('Webrtc connection has ' + webrtcState)
			} else {
				hangupCall()
			}
		}
	}, [webrtcState])

	// FUNCTION TO TOGGLE BETWEEN AUDIO AND VIDEO
	const toggleVideoCall = async () => {
		if (!getPeerConnection()) {
			console.error('Peer connection is not initialized.')
			return
		}
		try {
			createLog('webrtc_debug', 'toggling local video')
			let isVideoOn

			// REUSE THE EXISTING VIDEO TRACK WHENEVER POSSIBLE: ENABLE/DISABLE IT (THE SAME WAY THE
			// AGENT DOES) INSTEAD OF REMOVING AND RE-ADDING IT. A NEW TRACK WOULD CARRY A NEW SSRC ON
			// THE SAME M-LINE AND THE REMOTE PEER (libwebrtc) REJECTS THE RENEGOTIATION OFFER (#74).
			const existingVideoTrack = localStream.current?.getVideoTracks?.()?.[0]

			if (existingVideoTrack) {
				// SUBSEQUENT TOGGLES: JUST FLIP THE TRACK STATE, NO RENEGOTIATION.
				isVideoOn = !existingVideoTrack.enabled
				existingVideoTrack.enabled = isVideoOn

				// KEEP THE LOCAL SELF-VIEW IN SYNC (NOT SHOWN ON KIOSKS).
				if (!isDirectCallEnabled.current && myVideo.current) {
					myVideo.current.srcObject = isVideoOn ? new MediaStream([existingVideoTrack]) : null
				}
				setStream(localStream.current)
			} else {
				// FIRST TIME THE CAMERA IS TURNED ON: ADD THE TRACK AND RENEGOTIATE ONCE.
				createLog('webrtc_debug', 'start video stream')
				const streamStarted = await startStream('video')
				if (!streamStarted) {
					return
				}
				sendOfferToRemoteUser(remoteUserSocketId, remoteUsername, me, location).then()
				isVideoOn = true
			}

			isItVideoCall.current = isVideoOn
			setCallType(isVideoOn ? 'video' : 'audio')
			setIsMyVideoOn(isVideoOn)

			// EMIT UPDATEMYMEDIA EVENT WITH VIDEO STATUS TO SERVER
			socket.emit('updateMyMedia', {
				type: 'video',
				currentMediaStatus: isVideoOn,
				from: remoteUserSocketId,
			})
		} catch (e) {
			createLog('webrtc_debug', 'failed to toggle camera', 'error', e)
		}
	}

	// FUNCTION TO STOP VIDEO TRACK
	const stopVideoTrack = () => {
		// MAKE SURE STREAM IS AVAILABLE.
		if (localStream.current) {
			let peerConnection = getPeerConnection()
			localStream.current?.getVideoTracks()?.forEach((track) => {
				track.stop()
				localStream.current.removeTrack(track) // REMOVE FROM THE LOCAL STREAM
			})
			setStream(localStream.current)

			// IF PEER CONNECTION IS AVAILABLE THEN REMOVE THE VIDEO TRACK FROM PEER CONNECTION.
			if (peerConnection && peerConnection?.getSenders()?.length > 0) {
				peerConnection.getSenders().forEach((sender) => {
					if (sender.track && sender.track.kind === 'video') {
						peerConnection.removeTrack(sender)
					}
				})
			}
		}
		myVideo.current.srcObject = null

		isItVideoCall.current = false
	}

	// CHECK THE MEDIA DEVICES AND SHOW SWITCH CAMERA OPTION IF MULTIPLE CAMERAS ARE AVAILABLE.
	const checkMediaDevices = async () => {
		try {
			const devices = await navigator.mediaDevices.enumerateDevices()
			const videoInputDevices = devices.filter((device) => device.kind === 'videoinput')
			setSupportsMultipleCameras(videoInputDevices.length > 1)
		} catch (err) {
			console.error('Error enumerating devices:', err)
			setSupportsMultipleCameras(false)
		}
	}

	// EXPOSE THE FLIP-CAMERA BUTTON ONLY WHEN IT MAKES SENSE.
	useEffect(() => {
		checkMediaDevices().then()
	}, [])

	// INITIALIZE THE AUDIO STREAM. AND ADD AUDIO TRACK TO PEER.
	const startAudioOnlyCall = async () => {
		try {
			await startStream('audio')

			// SEND OFFER TO REMOTE USER. THE CALLER WILL TRIGGER THIS.
			sendOfferToRemoteUser(remoteUserSocketId, remoteUsername, me, location).then()
		} catch (e) {
			console.error('error in getting audio stream', e)
			let reason = callerCode.current + ' video stream not available.'
			leaveCall(reason, true)
		}
	}

	const startPeerConnection = (remote_user_id) => {
		let peerConnection = getPeerConnection() // GET EXISTING PEER CONNECTION.

		// IF PEER CONNECTION IS NOT AVAILABLE THEN CREATE A NEW PEER CONNECTION.
		if (peerConnection) {
			return peerConnection
		}
		peerConnection = setPeerConnection(remote_user_id, remoteUsername, me, location)

		// PEER EVENTS
		peerConnection.oniceconnectionstatechange = handleIceConnectionStateChangeEvent
		peerConnection.ontrack = (event) => handleOnTrackEvent(event, remote_user_id)
		peerConnection.onconnectionstatechange = (ev) => handleConnectionStateChangeEvent(ev, peerConnection)
		peerConnection.onicecandidateerror = (event) => {
			// WHICH SERVER FAILED, NOT JUST THAT ONE DID: WITH ONE STUN AND THREE TURN URLS
			// CONFIGURED, A BARE ERROR CODE (701 = THE SERVER COULD NOT BE REACHED) DOES NOT SAY
			// WHETHER THE RELAY IS DOWN OR ONLY A UDP PROBE WAS DROPPED, WHICH IS THE WHOLE
			// DIFFERENCE BETWEEN A BROKEN CALL AND A HARMLESS MESSAGE.
			console.error(`ICE Candidate Error: ${event.errorCode} - ${event.errorText} (${event.url || 'unknown server'})`)
		}
		// A FRESH PEER CONNECTION IS ABOUT TO NEGOTIATE: WATCH THAT THE MEDIA PATH ACTUALLY
		// CONNECTS, AND ESCALATE TO RELAY IF IT STALLS (SEE onMediaConnectWatchdog).
		armMediaConnectWatchdog()
		return peerConnection
	}

	const switchCam = async () => {
		// THIS SHOULD BE A VIDEO CALL TO SWITCH THE CAMERA.
		if (callType === 'audio') {
			createLog('webrtc_debug', 'tried to switch camera on audio call', 'error')
			return
		}
		let isItFrontCam = !frontCam

		// SET THE FACING MODE BASED ON frontCam STATE
		constraints.video.facingMode = isItFrontCam ? 'user' : 'environment'
		try {
			const currentStream = await navigator.mediaDevices.getUserMedia({ ...constraints.video })
			let peerConnection = getPeerConnection()

			// GET THE VIDEO TRACK AND SET THE STREAM OBJECT.
			let videoTrack = currentStream.getVideoTracks()[0]
			await peerConnection?.getSenders()?.filter((sender) => sender.track?.kind === 'video')[0].replaceTrack(videoTrack)
		} catch (error) {
			createLog('webrtc_debug', 'error flipping camera', error)
		}
		setfrontCam(!frontCam)
	}

	// THE CALLER WILL BE TRIGGERED TO END THE CALL.
	const leaveCall = async (reason, error = false) => {
		let call_id = callId || globals.call_id
		createLog('webrtc_debug', 'Leave call was triggered with reason: ', reason, 'call id is ' + call_id)
		const callDuration = sec
		terminateCallConnection()

		let endedPayload = {
			callendreason: reason ? reason : 'The caller has ended the call.',
			toHaveError: error,
			errorFrom: callerCode.current,
		}

		// A CALL THAT DIED BEFORE THE SERVER ISSUED AN ID HAS NOTHING TO CLOSE. Asking anyway sent
		// a null call_id to /updatecalltime and to the Firestore copy, which only produced errors
		// in both logs.
		if (call_id) {
			// CREATE THE UPDATE PAYLOAD OBJECT.
			const payload = {
				action: 'endtime',
				call_id: call_id,
			}
			httpRequest(`static/updatecalltime`, 'POST', payload).then()

			// STATUS UPDATE CALL ENDED IN REALTIME DATABASE.
			updateCallStatus(call_id, 'ENDED', endedPayload).then()

			// INSERT CALL DETAIL IN FIRESTORE.
			insertCallRecordIntoFirestore(call_id).then()
		} else {
			createLog('webrtc_debug', 'leaving a call with no call id, nothing to close', 'warn', reason)
		}

		socket.off('offer-candidate', onIceCandidateOffer)

		// IF ERROR OCCURS THEN NAVIGATE TO ERROR PAGE. OTHERWISE, NAVIGATE TO THANKS-CALL OR DASHBOARD PAGE.
		if (error) {
			socket.emit('errorOnCall', remoteUserSocketId, reason, call_id)

			// NAVIGATE TO ERROR PAGE.
			navigate('/callErrorHandler', { state: { message: reason + '. Please try again later.' } })
		} else {
			socket.emit('endCall', remoteUserSocketId)

			// NAVIGATE TO THANKS-CALL (CALLER) OR THE STAFF DASHBOARD (STAFF — ROADMAP #41).
			navigate(isCallStatus === 'OUT' ? '/thanks-call' : '/staff/dashboard', { state: { callId: call_id, duration: callDuration } })
		}
	}

	// THIS WILL BE TRIGGERED BY USER/CALLEE. END THE CALL FOR MOBILE USER AND INFORM CALLER ABOUT CALL END.
	const hangupCall = async () => {
		// SEND THE SERVER CALL ENDED EVENT IF SOCKET IS AVAILABLE
		socket.emit('endCall', remoteUserSocketId)
		socket.off('offer-candidate', onIceCandidateOffer)

		// READ THE ID ONCE AND ONLY CLOSE A CALL THAT HAS ONE (SAME REASON AS IN leaveCall).
		const endedCallId = globals.call_id
		if (endedCallId) {
			// CREATE THE UPDATE PAYLOAD OBJECT.
			const payload = {
				action: 'endtime',
				call_id: endedCallId,
			}
			httpRequest(`static/updatecalltime`, 'POST', payload).then()
			let endedPayload = {
				callendreason: 'The callee has ended the call.',
			}

			// STATUS UPDATE CALL ENDED IN REALTIME DATABASE AND FIRESTORE DATABASE.
			await updateCallStatus(endedCallId, 'ENDED', endedPayload)
			updateCallStatusByUserId(endedCallId, 'ENDED', user?.user_id).then()

			// INSERT CALL DETAIL IN FIRESTORE.
			await insertCallRecordIntoFirestore(endedCallId)
		}

		terminateCallConnection()
		navigate('/staff/dashboard')
	}

	// TERMINATE THE CALL CONNECTION.
	const terminateCallConnection = () => {
		// stop periodic pinging to server
		clearInterval(pingInterval)
		pingInterval = null

		// CANCEL ANY PENDING ICE RESTART AND RECOVERY DEADLINE: THE CALL IS OVER.
		if (iceRestartTimer.current) {
			clearTimeout(iceRestartTimer.current)
			iceRestartTimer.current = null
		}
		iceRestartAttempts.current = 0
		if (webrtcRecoveryTimer.current) {
			clearTimeout(webrtcRecoveryTimer.current)
			webrtcRecoveryTimer.current = null
		}
		clearMediaConnectWatchdog()
		if (mediaPathSnapshotTimer.current) {
			clearTimeout(mediaPathSnapshotTimer.current)
			mediaPathSnapshotTimer.current = null
		}
		relayEscalated.current = false
		handleResetButton()
		if (localStream.current) {
			createLog('webrtc_debug', 'stopping local stream')
			localStream.current.getTracks().forEach((track) => {
				track.stop()
				localStream.current.removeTrack(track)
			})
			localStream.current = null
			setStream(null)
		}
		if (myVideo.current) {
			myVideo.current.srcObject = null
		}
		connectionRef && connectionRef?.current?.destroy()
		call_detail_id.current = null
		globals.call_id = null
		closeUserPeerConnections()
		setCallEnded(true)
		setCallId(null)

		// UNSUBSCRIBE THE LISTENER TO CALL SOCKET EVENTS.
		unsubscribeToAllCallSocketEvents()
	}

	// UNSUBSCRIBE THE LISTENER TO CALL SOCKET EVENTS.
	const unsubscribeToAllCallSocketEvents = () => {
		// UNSUBSCRIBE REGARDLESS OF THE CONNECTION STATE: A DISCONNECTED SOCKET STILL KEEPS ITS
		// LISTENERS, AND A STALE connect HANDLER FROM A FINISHED CALL WOULD RE-DIAL ON RECONNECT.
		if (socket) {
			if (onSocketConnectRef.current) {
				socket.off('connect', onSocketConnectRef.current)
				onSocketConnectRef.current = null
			}

			// UNSUBSCRIBE FROM SOCKET EVENTS
			socket.off('staff-unavailable', handleStaffUnavailable)
			socket.off('staff-details', handleStaffDetail)
			socket.off('offer-candidate', onIceCandidateOffer)
			socket.off('offer', handleOffer)
			socket.off('updateUserMedia', handleUserMediaUpdate)
			socket.off('answer', handleAnswer)
			socket.off('endCall', handleEndCall)
			socket.off('errorOnCall', handleErrorOnCall)
			socket.off('getCallIdFromServer', getCallIdFromServer)
			socket.off('send_message', handleMessages)
			socket.off('send_message_response', handleMessagesResponse)
			socket.off('pong')
		}
	}

	// GET THE AUDIO OUTPUT DEVICES.
	async function getAudioOutputDevices() {
		try {
			const devices = await navigator.mediaDevices.enumerateDevices()
			return devices.filter((device) => device.kind === 'audiooutput')
		} catch (error) {
			console.error('Error enumerating devices:', error)
			return []
		}
	}

	// TOGGLE THE LOUDSPEAKER FOR AUDIO CALL
	const switchLoudSpeaker = async () => {
		try {
			const audioOutputDevices = await getAudioOutputDevices()

			const loudspeaker = audioOutputDevices.find((device) => device.deviceId === 'default')
			const phoneSpeakerDevice = audioOutputDevices.find((device) => device.deviceId !== 'default')

			if (loudspeaker || phoneSpeakerDevice) {
				if (userVideo.current) {
					const audioElement = userVideo.current

					if (!audioElement.setSinkId) {
						console.error('Browser does not support output device selection.')
						return
					}

					const selectedDevice = phoneSpeaker === 'external' ? loudspeaker.deviceId : phoneSpeakerDevice.deviceId

					try {
						await audioElement.setSinkId(selectedDevice)
					} catch (error) {
						console.error('Error setting sink ID:', error)
					}
				} else {
					console.error('userVideo not found.')
				}
			} else {
				console.error('Suitable audio output devices not found.')
			}

			// TOGGLE THE SPEAKER STATE
			setPhoneSpeaker((prev) => (prev === 'external' ? 'phone' : 'external'))
		} catch (error) {
			console.error('Error switching loudspeaker:', error)
		}
	}

	return (
		<VideoContext.Provider
			value={{
				switchCam,
				isSafariBrowser,
				staffTimeOut,
				callRejected,
				sec,
				call,
				callAccepted,
				myVideo,
				userVideo,
				userAudio,
				stream,
				remoteUsername,
				callEnded,
				me,
				leaveCall,
				setRemoteUserSocketId,
				username,
				isMyVideoOn,
				setIsMyVideoOn,
				userVdoStatus,
				setUserVdoStatus,
				myMicStatus,
				userMicStatus,
				updateMic,
				remoteUserSocketId,
				staffUnavailable,
				message,
				callStatus,
				queuePosition,
				webrtcState,
				callId,
				hangupCall,
				callType,
				isCallStatus,
				toggleVideoCall,
				switchLoudSpeaker,
				phoneSpeaker,
				isItVideoCall,
				supportsMultipleCameras,
				isDirectCallEnabled,
				isCallConnected,
				isHideOptionButtons,
			}}
		>
			<>{children}</>
		</VideoContext.Provider>
	)
}

export default VideoState
